"use strict";
/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
Object.defineProperty(exports, "__esModule", { value: true });
exports.Session = void 0;
exports.startSession = startSession;
const codegen_1 = require("@azure-tools/codegen");
const js_yaml_1 = require("js-yaml");
const types_1 = require("./types");
const safeEval = (0, codegen_1.createSandbox)();
class Session {
    /* @internal */ constructor(options) {
        this.errorCount = 0;
        this._debug = false;
        this._verbose = false;
        this.service = options.host;
        this.filename = options.filename;
        this.model = options.model;
        this.configuration = options.configuration;
        this._debug = options.configuration.debug;
        this._verbose = options.configuration.verbose;
        this.context = {
            $config: options.configuration,
            $lib: {
                // eslint-disable-next-line @typescript-eslint/no-require-imports
                path: require("path"),
            },
        };
    }
    async readFile(filename) {
        return this.service.readFile(filename);
    }
    async getValue(key, defaultValue) {
        let value = await this.service.getValue(key);
        // try as a safe eval execution.
        if (value === null || value === undefined) {
            try {
                value = safeEval(key, this.context);
            }
            catch (_a) {
                value = null;
            }
        }
        if (defaultValue === undefined && value === null) {
            throw new Error(`No value for configuration key '${key}' was provided`);
        }
        // ensure that any content variables are resolved at the end.
        return (value !== null ? value : defaultValue);
    }
    async listInputs(artifactType) {
        return this.service.listInputs(artifactType);
    }
    async protectFiles(path) {
        return this.service.protectFiles(path);
    }
    writeFile(options) {
        return this.service.writeFile(options);
    }
    message(message) {
        if (message.Channel === types_1.Channel.Debug && this._debug === false) {
            return;
        }
        if (message.Channel === types_1.Channel.Verbose && this._verbose === false) {
            return;
        }
        return this.service.message(message);
    }
    checkpoint() {
        if (this.errorCount > 0) {
            throw new Error(`${this.errorCount} errors occured -- cannot continue.`);
        }
    }
    debug(message) {
        this.msg("debug", message);
    }
    verbose(message) {
        this.msg("verbose", message);
    }
    info(message) {
        this.msg("info", message);
    }
    warning(message, key, source, details) {
        this.msg("warning", message, key, source, details);
    }
    error(message, key, source, details) {
        this.errorCount++;
        this.msg("error", message, key, source, details);
    }
    fatal(message, key, source, details) {
        this.errorCount++;
        this.msg("fatal", message, key, source, details);
    }
    /**
     * @deprecated use #info
     */
    log(message) {
        this.info(message);
    }
    msg(level, message, key, source, details) {
        const sourcePosition = source ? getPosition(this.filename, source) : undefined;
        this.service.logger.log({
            level,
            message,
            key,
            source: sourcePosition,
            details,
        });
    }
}
exports.Session = Session;
async function getModel(service, yamlSchema = js_yaml_1.DEFAULT_SCHEMA, artifactType) {
    const files = await service.listInputs(artifactType);
    const filename = files[0];
    if (files.length === 0) {
        throw new Error("Inputs missing.");
    }
    const content = await service.readFile(filename);
    return {
        filename,
        model: (0, codegen_1.deserialize)(content, filename, yamlSchema),
    };
}
async function startSession(host, schema = js_yaml_1.DEFAULT_SCHEMA, artifactType) {
    // This is just to make it work with older version so the older method definition is also valid.
    if ((artifactType === null || artifactType === void 0 ? void 0 : artifactType.constructor.name) === "Schema") {
        schema = artifactType;
        artifactType = undefined;
    }
    const { model, filename } = await getModel(host, schema, artifactType);
    const configuration = await host.getValue("");
    return new Session({ host, filename, model, configuration });
}
function getPosition(document, source) {
    if (typeof source === "string") {
        return { Position: { path: source }, document };
    }
    if (source[codegen_1.ShadowedNodePath]) {
        return { Position: { path: source[codegen_1.ShadowedNodePath] }, document };
    }
    if ("path" in source || "line" in source) {
        return { Position: source, document };
    }
    return undefined;
}
//# sourceMappingURL=session.js.map